import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Flame, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "bg-[#262D38] border-[#2A313C] focus:border-[#E11D48] focus-visible:ring-[#E11D48]/30 text-white placeholder:text-slate-500 rounded-xl";

export default function StaffLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/staff/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center spark-grid-bg px-5">
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full spark-glow-pulse pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(225,29,72,0.15), transparent 70%)" }}
      />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-[#E11D48] to-[#F59E0B] shadow-[0_0_20px_rgba(225,29,72,0.4)]">
            <Flame className="w-5 h-5 text-white" fill="white" />
          </span>
          <span className="font-heading font-extrabold text-xl text-white">Sparkd</span>
        </Link>

        <div className="rounded-2xl border border-[#2A313C] bg-[#1F242D] p-8">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-[#E11D48]" />
            <span className="text-xs uppercase font-semibold tracking-[0.2em] text-[#E11D48]">Staff Portal</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-white">Sign in</h1>
          <p className="mt-1 text-sm text-slate-400">Access the support & compliance dashboard.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@sparkd-app.com" className={inputCls} data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className={inputCls} data-testid="login-password" />
            </div>
            {error && (
              <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rounded-xl py-6 text-base font-semibold bg-gradient-to-r from-[#E11D48] to-[#F59E0B] text-white hover:shadow-[0_0_24px_rgba(225,29,72,0.4)] transition-all">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-slate-400 hover:text-[#E11D48]" data-testid="forgot-link">
                Forgot password?
              </Link>
            </div>
          </form>
        </div>
        <p className="mt-6 text-center text-sm text-slate-500">
          <Link to="/" className="hover:text-slate-300">← Back to support portal</Link>
        </p>
      </div>
    </div>
  );
}
